// Block D: Add and Edit contact, on the v2 Modal (full screen on a phone, by the kit).
//
// It used to size itself ONCE from window.innerWidth at render, so rotating a tablet or resizing a
// window left the wrong layout until it was closed. The layout now follows useBreakpoint().
//
// Every field the old form had is kept: name, email, phone, company, tags (or a Personal category),
// notes, default invoice currency, and "new contact". The Add form's "New" chip IS the isNew flag
// (on by default for a contact added by hand, as in the mockup), so the New tab keeps one meaning.
import React, { useMemo, useState } from 'react';
import { Modal, Button, Field, Input, Textarea, Select, Chip, useBreakpoint } from '../../ui';
import { PRESET_TAGS, PERSONAL_CATEGORIES, tagColor } from './contactTags';
import './Contacts.css';

export const CONTACT_CURRENCIES = [
  { code: '', label: 'None' },
  { code: 'GBP', label: 'GBP, British Pound (£)' },
  { code: 'USD', label: 'USD, US Dollar ($)' },
  { code: 'EUR', label: 'EUR, Euro (€)' },
  { code: 'CAD', label: 'CAD, Canadian Dollar (CA$)' },
  { code: 'AUD', label: 'AUD, Australian Dollar (A$)' },
  { code: 'CHF', label: 'CHF, Swiss Franc' },
  { code: 'JPY', label: 'JPY, Japanese Yen (¥)' },
  { code: 'SEK', label: 'SEK, Swedish Krona' },
  { code: 'NOK', label: 'NOK, Norwegian Krone' },
  { code: 'DKK', label: 'DKK, Danish Krone' },
  { code: 'NZD', label: 'NZD, New Zealand Dollar (NZ$)' },
  { code: 'SGD', label: 'SGD, Singapore Dollar (S$)' },
  { code: 'AED', label: 'AED, UAE Dirham' },
  { code: 'ZAR', label: 'ZAR, South African Rand (R)' },
];

function initForm(contact) {
  return {
    name: contact?.name ?? '',
    email: contact?.email ?? '',
    phone: contact?.phone ?? '',
    company: contact?.company ?? '',
    tags: contact?.tags ?? [],
    notes: contact?.notes ?? '',
    // A contact added by hand starts as New (the mockup's default); an edit keeps what it was.
    isNew: contact ? !!contact.isNew : true,
    source: contact?.source ?? 'manual',
    defaultCurrency: contact?.defaultCurrency ?? '',
    customTag: '',
    personalCategory: (contact?.tags ?? []).find((t) => PERSONAL_CATEGORIES.includes(t)) ?? '',
  };
}

// "Heads up: Ada (ada@example.com) already exists." Matched by email, else by the exact name.
export function duplicateOf(form, contacts, selfId) {
  const email = form.email.trim().toLowerCase();
  const name = form.name.trim().toLowerCase();
  const others = (contacts || []).filter((c) => c.id !== selfId);
  // Decision 79: an address a merged contact keeps as an alias belongs to that contact too.
  const answersTo = (c) => [c.email, ...(Array.isArray(c.emailAliases) ? c.emailAliases : [])].some((e) => String(e || '').trim().toLowerCase() === email);
  return (email && others.find(answersTo))
    || (name && others.find((c) => String(c.name || '').trim().toLowerCase() === name))
    || null;
}

export default function ContactFormModal({ contact, onSave, onClose, availableTags, isPersonal, contacts = [], tagColors }) {
  const formTags = availableTags || PRESET_TAGS;
  const [form, setForm] = useState(() => initForm(contact));
  const [saving, setSaving] = useState(false);
  const phone = useBreakpoint() === 'phone';
  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));
  const dupe = useMemo(() => duplicateOf(form, contacts, contact?.id), [form, contacts, contact?.id]);

  const toggleTag = (tag) => setForm((f) => ({ ...f, tags: f.tags.includes(tag) ? f.tags.filter((t) => t !== tag) : [...f.tags, tag] }));
  const addCustomTag = () => {
    const t = form.customTag.trim();
    if (!t || form.tags.includes(t)) return;
    setForm((f) => ({ ...f, tags: [...f.tags, t], customTag: '' }));
  };

  async function handleSave() {
    if (!form.name.trim() || saving) return;
    // In personal mode: the single category goes into tags, keeping any non-category tags.
    const finalTags = isPersonal
      ? [...(form.personalCategory ? [form.personalCategory] : []), ...form.tags.filter((t) => !PERSONAL_CATEGORIES.includes(t))]
      : form.tags;
    setSaving(true);
    try {
      await onSave({
        id: contact?.id ?? Date.now(),
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        tags: finalTags,
        notes: form.notes.trim(),
        isNew: isPersonal ? !!contact?.isNew : form.isNew,
        source: form.source,
        defaultCurrency: form.defaultCurrency || null,
        interactions: contact?.interactions ?? [],
        createdAt: contact?.createdAt ?? new Date().toISOString(),
      });
    } finally {
      setSaving(false);
    }
  }

  const extraTags = form.tags.filter((t) => !formTags.includes(t) && !PERSONAL_CATEGORIES.includes(t));

  return (
    <Modal
      open
      onClose={onClose}
      title={contact ? 'Edit contact' : 'Add contact'}
      color="pastel-blue"
      width={520}
      footer={<>
        <Button variant="tertiary" onClick={onClose}>Cancel</Button>
        <Button variant="confirm" onClick={handleSave} disabled={!form.name.trim() || saving}>
          {saving ? 'Saving…' : contact ? 'Save changes' : 'Save contact'}
        </Button>
      </>}
    >
      <Field label="Name" required>
        <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Full name" autoComplete="off" data-autofocus="" />
      </Field>
      <div className={phone ? 'contacts-form__stack' : 'contacts-form__pair'}>
        <Field label="Email">
          <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="email@example.com" autoComplete="off" />
        </Field>
        <Field label="Phone">
          <Input type="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+44..." autoComplete="off" />
        </Field>
      </div>
      {dupe && (
        <p className="contacts-dupe" role="status" data-testid="contact-form-duplicate">
          Heads up: {dupe.name}{dupe.email ? ` (${dupe.email})` : ''} already exists.
        </p>
      )}
      <Field label="Company">
        <Input value={form.company} onChange={(e) => set('company', e.target.value)} placeholder="Company (optional)" autoComplete="off" />
      </Field>

      {isPersonal ? (
        <div className="ui-field">
          <span className="ui-field__label">Category</span>
          <div className="contacts-chips">
            {PERSONAL_CATEGORIES.map((cat) => (
              <Chip key={cat} selected={form.personalCategory === cat} color={tagColor(cat)}
                onClick={() => set('personalCategory', form.personalCategory === cat ? '' : cat)}>{cat}</Chip>
            ))}
          </div>
        </div>
      ) : (
        <div className="ui-field">
          <span className="ui-field__label">Tags</span>
          <div className="contacts-chips">
            <Chip selected={form.isNew} color={tagColor('New')} onClick={() => set('isNew', !form.isNew)}>New</Chip>
            {formTags.map((tag) => (
              <Chip key={tag} selected={form.tags.includes(tag)} color={tagColor(tag, tagColors)} onClick={() => toggleTag(tag)}>{tag}</Chip>
            ))}
            {extraTags.map((tag) => (
              <Chip key={tag} selected color={tagColor(tag, tagColors)} onClick={() => toggleTag(tag)} aria-label={`Remove ${tag}`}>{tag} ✕</Chip>
            ))}
          </div>
          <div className="contacts-form__add-tag">
            <Input value={form.customTag} onChange={(e) => set('customTag', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomTag(); } }}
              placeholder="Add a tag for this contact" aria-label="Add a tag for this contact" />
            <Button variant="tertiary" size="sm" onClick={addCustomTag} disabled={!form.customTag.trim()}>Add</Button>
          </div>
        </div>
      )}

      <Field label="Notes">
        <Textarea rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Any notes about this contact" />
      </Field>

      {!isPersonal && (
        <Field label="Default invoice currency">
          <Select value={form.defaultCurrency} onChange={(e) => set('defaultCurrency', e.target.value)}
            options={CONTACT_CURRENCIES.map((c) => ({ value: c.code, label: c.label }))} />
        </Field>
      )}
    </Modal>
  );
}
